import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import { Plus, Check, Lock, Unlock, X, ChevronDown, ChevronUp, Calculator, FileText, AlertTriangle, Archive } from 'lucide-react';

const Field = ({ label, children }) => (
  <div><label className="text-slate-400 text-xs font-medium mb-1 block">{label}</label>{children}</div>
);
const Input = (props) => <input {...props} className={`w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A] ${props.className || ''}`} />;

const STATUT_COLORS = {
  a_saisir: { bg: 'bg-slate-500/15', text: 'text-slate-400', label: 'A saisir' },
  en_cours: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'En cours' },
  a_valider: { bg: 'bg-[#E8A020]/15', text: 'text-[#E8A020]', label: 'A valider' },
  valide: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Valide' },
  anomalie: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Anomalie' },
};

const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') : '-';
const MOIS_NOMS = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

export default function VariablesPaiePage() {
  const { mpFetch, can } = useMoanaPaye();
  const [entreprises, setEntreprises] = useState([]);
  const [selectedEnt, setSelectedEnt] = useState('');
  const [periodes, setPeriodes] = useState([]);
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(2026);
  const [variables, setVariables] = useState([]);
  const [editVar, setEditVar] = useState(null);
  const [bulletinResult, setBulletinResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [periodeStatut, setPeriodeStatut] = useState(null);
  const [periodeId, setPeriodeId] = useState(null);

  useEffect(() => {
    mpFetch('/api/moana-paye/entreprises').then(d => { setEntreprises(d); if (d.length) setSelectedEnt(d[0].id); }).catch(console.error);
  }, []);

  const loadData = () => {
    if (!selectedEnt) return;
    mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/periodes`).then(d => {
      setPeriodes(d);
      const cur = d.find(p => p.mois === mois && p.annee === annee);
      setPeriodeStatut(cur?.statut || null);
      setPeriodeId(cur?.id || null);
    }).catch(console.error);
    mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/variables/${annee}/${mois}`).then(setVariables).catch(() => setVariables([]));
  };

  useEffect(loadData, [selectedEnt, mois, annee]);

  const ouvrirPeriode = async () => {
    setSaving(true);
    try {
      await mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/periodes/${annee}/${mois}`, { method: 'POST' });
      loadData();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const saveVariable = async () => {
    setSaving(true);
    try {
      await mpFetch(`/api/moana-paye/variables/${editVar.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          heures_normales: editVar.heures_normales,
          heures_sup_25: editVar.heures_sup_25,
          heures_sup_50: editVar.heures_sup_50,
          heures_nuit: editVar.heures_nuit,
          heures_dimanche: editVar.heures_dimanche,
          heures_ferie: editVar.heures_ferie,
          heures_1er_mai: editVar.heures_1er_mai,
          primes: editVar.primes,
          jours_maladie: editVar.jours_maladie,
          numero_arret_annee: editVar.numero_arret_annee,
          jours_conges_pris: editVar.jours_conges_pris,
          jours_absence_injustifiee: editVar.jours_absence_injustifiee,
          avantages_nature: editVar.avantages_nature,
          regularisations: editVar.regularisations,
          reconduire: editVar.reconduire,
        })
      });
      loadData();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const calculerBulletin = async (vid) => {
    try {
      const res = await mpFetch(`/api/moana-paye/variables/${vid}/calculer`, { method: 'POST' });
      setBulletinResult(res);
      loadData();
    } catch (err) { alert(err.message); }
  };

  const validerBulletin = async (vid) => {
    try {
      await mpFetch(`/api/moana-paye/variables/${vid}/valider`, { method: 'POST' });
      loadData();
    } catch (err) { alert(err.message); }
  };

  const cloturerPeriode = async () => {
    if (!window.confirm(`Cloturer la periode ${MOIS_NOMS[mois]} ${annee} ? Les bulletins seront deposes dans le coffre-fort.`)) return;
    try {
      await mpFetch(`/api/moana-paye/periodes/${periodeId}/cloturer`, { method: 'POST' });
      loadData();
    } catch (err) { alert(err.message); }
  };

  const decloturer = async () => {
    const motif = prompt("Motif de decloture (obligatoire) :");
    if (!motif || motif.trim().length < 5) return alert("Motif obligatoire (5 car. min)");
    try {
      await mpFetch(`/api/moana-paye/periodes/${periodeId}/decloturer`, { method: 'POST', body: JSON.stringify({ motif }) });
      loadData();
    } catch (err) { alert(err.message); }
  };

  const setEV = (k, v) => setEditVar(ev => ({ ...ev, [k]: v }));
  const addPrime = () => setEV('primes', [...(editVar.primes || []), { libelle: '', montant: 0 }]);
  const addRegul = () => setEV('regularisations', [...(editVar.regularisations || []), { motif: '', montant: 0, mois_reference: '' }]);

  return (
    <div data-testid="mp-variables">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Variables de paie</h2>
        <div className="flex items-center gap-2">
          <select value={mois} onChange={e => setMois(parseInt(e.target.value))} className="bg-[#1a2332] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
            {MOIS_NOMS.slice(1).map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
          </select>
          <Input type="number" value={annee} onChange={e => setAnnee(parseInt(e.target.value))} className="w-20" />
          {periodeStatut === 'valide' ? (
            can('cloturer_periode') && <button onClick={decloturer} className="flex items-center gap-1 bg-red-500/20 text-red-400 px-3 py-2 rounded-lg text-sm"><Unlock className="w-4 h-4" />Decloturer</button>
          ) : periodeStatut ? (
            can('cloturer_periode') && <button onClick={cloturerPeriode} className="flex items-center gap-1 bg-green-500/20 text-green-400 px-3 py-2 rounded-lg text-sm"><Lock className="w-4 h-4" />Cloturer</button>
          ) : (
            can('ouvrir_periode') && <button onClick={ouvrirPeriode} disabled={saving} className="flex items-center gap-1 bg-[#2D5A5A] text-white px-3 py-2 rounded-lg text-sm"><Plus className="w-4 h-4" />Ouvrir</button>
          )}
        </div>
      </div>

      {/* Statut periode */}
      {periodeStatut && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${STATUT_COLORS[periodeStatut]?.bg} ${STATUT_COLORS[periodeStatut]?.text}`}>
          {periodeStatut === 'valide' && <Lock className="w-4 h-4" />}
          Periode {MOIS_NOMS[mois]} {annee} — {STATUT_COLORS[periodeStatut]?.label}
        </div>
      )}

      {!periodeStatut ? (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl p-10 text-center">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Periode non ouverte</p>
          <button onClick={ouvrirPeriode} className="mt-3 text-[#E8A020] text-sm hover:underline">Ouvrir {MOIS_NOMS[mois]} {annee}</button>
        </div>
      ) : (
        <div className="space-y-2">
          {variables.map(v => {
            const sc = STATUT_COLORS[v.statut] || STATUT_COLORS.a_saisir;
            return (
              <div key={v.id} className="bg-[#1a2332] border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#2D5A5A]/20 flex items-center justify-center text-[#2D5A5A] font-bold text-sm">{v.prenom?.[0]}{v.nom?.[0]}</div>
                    <div>
                      <div className="text-white font-medium text-sm">{v.prenom} {v.nom}</div>
                      <div className="text-slate-500 text-xs">{v.poste_occupe} &middot; {v.salaire_base ? fmt(v.salaire_base) + ' F' : '-'} &middot; {v.type_contrat}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {v.alertes && v.alertes.length > 0 && <AlertTriangle className="w-4 h-4 text-[#E8A020]" />}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>{sc.label}</span>
                    {v.resultat_bulletin && <span className="text-xs text-slate-500">Net: {fmt(v.resultat_bulletin.net_a_payer)} F</span>}
                    <div className="flex gap-1 ml-2">
                      {periodeStatut !== 'valide' && (
                        <>
                          <button onClick={() => setEditVar({...v})} className="text-xs bg-white/5 hover:bg-white/10 text-slate-300 px-2 py-1 rounded" title="Saisir">Saisir</button>
                          {can('calculer_bulletin') && <button onClick={() => calculerBulletin(v.id)} className="text-xs bg-[#2D5A5A]/30 hover:bg-[#2D5A5A]/50 text-[#2D5A5A] px-2 py-1 rounded flex items-center gap-1"><Calculator className="w-3 h-3" />Calculer</button>}
                          {v.statut === 'a_valider' && can('valider_bulletin') && <button onClick={() => validerBulletin(v.id)} className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded flex items-center gap-1"><Check className="w-3 h-3" />Valider</button>}
                        </>
                      )}
                      {v.resultat_bulletin && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/moana-paye/bulletins/${v.id}/pdf`} target="_blank" rel="noreferrer" className="text-xs bg-[#E8A020]/20 text-[#E8A020] px-2 py-1 rounded flex items-center gap-1"><FileText className="w-3 h-3" />PDF</a>
                      )}
                    </div>
                  </div>
                </div>
                {/* Resume rapide */}
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                  <span>{v.heures_normales}h norm.</span>
                  {v.heures_sup_25 > 0 && <span className="text-blue-400">+{v.heures_sup_25}h HS25%</span>}
                  {v.heures_sup_50 > 0 && <span className="text-blue-400">+{v.heures_sup_50}h HS50%</span>}
                  {v.jours_maladie > 0 && <span className="text-orange-400">{v.jours_maladie}j maladie</span>}
                  {v.jours_conges_pris > 0 && <span className="text-green-400">{v.jours_conges_pris}j CP</span>}
                  {v.primes?.length > 0 && <span className="text-[#E8A020]">{v.primes.length} prime(s)</span>}
                  {v.regularisations?.length > 0 && <span className="text-purple-400">{v.regularisations.length} regul.</span>}
                  {v.reconduire && <span className="text-cyan-400">Reconduit</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal saisie variables */}
      {editVar && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setEditVar(null)}>
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">{editVar.prenom} {editVar.nom} — {MOIS_NOMS[mois]} {annee}</h3>
              <button onClick={() => setEditVar(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
              {/* M3.1 Heures */}
              <div>
                <h4 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase mb-2">M3.1 — Heures</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Field label="Normales"><Input type="number" step="0.5" value={editVar.heures_normales || 0} onChange={e => setEV('heures_normales', parseFloat(e.target.value))} /></Field>
                  <Field label="HS 25%"><Input type="number" step="0.5" value={editVar.heures_sup_25 || 0} onChange={e => setEV('heures_sup_25', parseFloat(e.target.value))} /></Field>
                  <Field label="HS 50%"><Input type="number" step="0.5" value={editVar.heures_sup_50 || 0} onChange={e => setEV('heures_sup_50', parseFloat(e.target.value))} /></Field>
                  <Field label="Nuit"><Input type="number" step="0.5" value={editVar.heures_nuit || 0} onChange={e => setEV('heures_nuit', parseFloat(e.target.value))} /></Field>
                  <Field label="Dimanche"><Input type="number" step="0.5" value={editVar.heures_dimanche || 0} onChange={e => setEV('heures_dimanche', parseFloat(e.target.value))} /></Field>
                  <Field label="Ferie"><Input type="number" step="0.5" value={editVar.heures_ferie || 0} onChange={e => setEV('heures_ferie', parseFloat(e.target.value))} /></Field>
                  <Field label="1er Mai"><Input type="number" step="0.5" value={editVar.heures_1er_mai || 0} onChange={e => setEV('heures_1er_mai', parseFloat(e.target.value))} /></Field>
                </div>
              </div>
              {/* M3.2 Primes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">M3.2 — Primes</h4>
                  <button onClick={addPrime} className="text-[#2D5A5A] text-xs flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>
                </div>
                {(editVar.primes || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1">
                    <Input value={p.libelle} onChange={e => { const arr = [...editVar.primes]; arr[i] = {...arr[i], libelle: e.target.value}; setEV('primes', arr); }} placeholder="Libelle" className="flex-1" />
                    <Input type="number" value={p.montant} onChange={e => { const arr = [...editVar.primes]; arr[i] = {...arr[i], montant: parseFloat(e.target.value) || 0}; setEV('primes', arr); }} className="w-28" />
                    <button onClick={() => setEV('primes', editVar.primes.filter((_, idx) => idx !== i))} className="text-slate-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              {/* M3.3 Absences */}
              <div>
                <h4 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase mb-2">M3.3 — Absences</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Field label="Maladie (j. cal.)"><Input type="number" min={0} value={editVar.jours_maladie || 0} onChange={e => setEV('jours_maladie', parseInt(e.target.value) || 0)} /></Field>
                  <Field label="N arret annee"><Input type="number" min={1} value={editVar.numero_arret_annee || 1} onChange={e => setEV('numero_arret_annee', parseInt(e.target.value) || 1)} /></Field>
                  <Field label="CP pris (j.)"><Input type="number" min={0} step="0.5" value={editVar.jours_conges_pris || 0} onChange={e => setEV('jours_conges_pris', parseFloat(e.target.value) || 0)} /></Field>
                  <Field label="Abs. injust. (j.)"><Input type="number" min={0} value={editVar.jours_absence_injustifiee || 0} onChange={e => setEV('jours_absence_injustifiee', parseFloat(e.target.value) || 0)} /></Field>
                </div>
              </div>
              {/* M3.4 Avantages */}
              <div>
                <h4 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase mb-2">M3.4 — Avantages en nature</h4>
                {(editVar.avantages_nature || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1">
                    <span className="text-slate-400 text-sm flex-1">{a.type}</span>
                    <Input type="number" value={a.montant} onChange={e => { const arr = [...editVar.avantages_nature]; arr[i] = {...arr[i], montant: parseFloat(e.target.value) || 0}; setEV('avantages_nature', arr); }} className="w-28" />
                  </div>
                ))}
                {(!editVar.avantages_nature || editVar.avantages_nature.length === 0) && <p className="text-slate-600 text-xs">Aucun avantage en nature</p>}
              </div>
              {/* M3.6 Regularisations */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">M3.6 — Regularisations</h4>
                  <button onClick={addRegul} className="text-[#2D5A5A] text-xs flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>
                </div>
                {(editVar.regularisations || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1">
                    <Input value={r.motif} onChange={e => { const arr = [...editVar.regularisations]; arr[i] = {...arr[i], motif: e.target.value}; setEV('regularisations', arr); }} placeholder="Motif (ex: Rappel salaire 03/2026)" className="flex-1" />
                    <Input type="number" value={r.montant} onChange={e => { const arr = [...editVar.regularisations]; arr[i] = {...arr[i], montant: parseFloat(e.target.value) || 0}; setEV('regularisations', arr); }} className="w-28" />
                    <button onClick={() => setEV('regularisations', editVar.regularisations.filter((_, idx) => idx !== i))} className="text-slate-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              {/* Reconduire */}
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={editVar.reconduire || false} onChange={e => setEV('reconduire', e.target.checked)} className="rounded" />
                Reconduire ces variables le mois suivant
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-white/10">
              <button onClick={() => setEditVar(null)} className="px-4 py-2 text-sm text-slate-400">Annuler</button>
              <button onClick={saveVariable} disabled={saving} className="px-5 py-2 bg-[#2D5A5A] text-white text-sm font-medium rounded-lg disabled:opacity-50">{saving ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal resultat bulletin */}
      {bulletinResult && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setBulletinResult(null)}>
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 w-full max-w-xl my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Resultat du calcul</h3>
              <button onClick={() => setBulletinResult(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-slate-400 text-xs mb-1">BRUT</div>
                {bulletinResult.lignes_brut?.map((l, i) => (
                  <div key={i} className="flex justify-between text-sm"><span className="text-white">{l.rubrique}</span><span className={l.montant < 0 ? 'text-red-400' : 'text-white'}>{fmt(l.montant)} F</span></div>
                ))}
                <div className="flex justify-between text-sm font-bold mt-1 pt-1 border-t border-white/10"><span className="text-[#E8A020]">TOTAL BRUT</span><span className="text-[#E8A020]">{fmt(bulletinResult.total_brut)} F</span></div>
              </div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Cotisations salariales CPS</span><span className="text-white">-{fmt(bulletinResult.total_cotisations_salariales)} F</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">CST</span><span className="text-white">-{fmt(bulletinResult.total_cst)} F</span></div>
              <div className="bg-[#2D5A5A] rounded-lg p-4 text-center">
                <div className="text-white/70 text-xs uppercase tracking-wider">Net a payer</div>
                <div className="text-2xl font-bold text-white">{fmt(bulletinResult.net_a_payer)} <span className="text-sm text-white/70">F CFP</span></div>
              </div>
              {bulletinResult.alertes?.length > 0 && (
                <div className="bg-[#E8A020]/10 border border-[#E8A020]/30 rounded-lg p-3">
                  {bulletinResult.alertes.map((a, i) => <div key={i} className="text-[#E8A020] text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{a}</div>)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
